import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Chip,
  Grid,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import { State } from "country-state-city";
import Autocomplete from "@mui/material/Autocomplete";

import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  getEclinicDoctors,
  approveDoctor,
  rejectDoctor,
  getEclinicStats,
} from "./EclinicService";

const EclinicDoctors = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [stats, setStats] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    programmeType: "",
    state: "",
  });

  const indianStates = State.getStatesOfCountry("IN");

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const response = await getEclinicDoctors({ page, limit: 10, ...filters });
      setDoctors(response?.data?.data || []);
      setTotalPages(response?.data?.totalPages || 1);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch doctors");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getEclinicStats();
      setStats(response?.data?.data || {});
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, [page, filters]);

  useEffect(() => {
    fetchStats();
  }, []);

  const handleApprove = async (id) => {
    try {
      await approveDoctor(id, { remarks: "Approved by admin" });
      toast.success("Doctor approved successfully");
      fetchDoctors();
      fetchStats();
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
  };

  const handleReject = async (id) => {
    try {
      await rejectDoctor(id, { remarks: "Rejected by admin" });
      toast.success("Doctor rejected successfully");
      fetchDoctors();
      fetchStats();
    } catch (error) {
      toast.error(error?.response?.data?.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved": return "success";
      case "Rejected": return "error";
      default: return "warning";
    }
  };

  // ── Mobile Card ──
  const MobileCard = ({ doctor }) => (
    <Card
      sx={{
        p: 2,
        mb: 2,
        borderRadius: "14px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
        <Box>
          <Typography fontWeight={700} fontSize={15} color="#111827">
            {doctor?.fullName}
          </Typography>
          <Typography fontSize={12} color="text.secondary">
            {doctor?.mobileNumber}
          </Typography>
        </Box>
        <Chip
          label={doctor?.status}
          color={getStatusColor(doctor?.status)}
          size="small"
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1,
          mb: 1.5,
          p: 1.5,
          backgroundColor: "#f8fafc",
          borderRadius: "10px",
        }}
      >
        <Box>
          <Typography fontSize={11} color="text.secondary">Registration ID</Typography>
          <Typography fontSize={12} fontWeight={600}>{doctor?.registrationId}</Typography>
        </Box>
        <Box>
          <Typography fontSize={11} color="text.secondary">Programme</Typography>
          <Typography fontSize={12} fontWeight={600}>{doctor?.programmeType}</Typography>
        </Box>
        <Box>
          <Typography fontSize={11} color="text.secondary">Qualification</Typography>
          <Typography fontSize={12} fontWeight={600}>{doctor?.qualification}</Typography>
        </Box>
        <Box>
          <Typography fontSize={11} color="text.secondary">State</Typography>
          <Typography fontSize={12} fontWeight={600}>{doctor?.state}</Typography>
        </Box>
        <Box>
          <Typography fontSize={11} color="text.secondary">Submitted</Typography>
          <Typography fontSize={12} fontWeight={600}>
            {new Date(doctor?.createdAt).toLocaleDateString()}
          </Typography>
        </Box>
      </Box>

      <Box display="flex" gap={1} flexWrap="wrap">
        <Button
          variant="outlined"
          size="small"
          startIcon={<VisibilityIcon />}
          onClick={() => navigate(`/eclinic/${doctor?._id}`)}
          sx={{ flex: 1, textTransform: "none", borderRadius: "8px" }}
        >
          View
        </Button>
        {doctor?.status === "Pending" && (
          <>
            <Button
              color="success"
              variant="contained"
              size="small"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleApprove(doctor?._id)}
              sx={{ flex: 1, textTransform: "none", borderRadius: "8px" }}
            >
              Approve
            </Button>
            <Button
              color="error"
              variant="contained"
              size="small"
              startIcon={<CancelIcon />}
              onClick={() => handleReject(doctor?._id)}
              sx={{ flex: 1, textTransform: "none", borderRadius: "8px" }}
            >
              Reject
            </Button>
          </>
        )}
      </Box>
    </Card>
  );

  return (
    <Box p={{ xs: 2, md: 3 }} sx={{ background: "#f8fafc", minHeight: "100vh" }}>
      <Typography variant="h5" fontWeight={700} mb={3} color="#111827">
        E-Clinic Doctor Registrations
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: "Total Doctors", value: stats?.totalRegistered || 0, color: "#111827" },
          { label: "Pending", value: stats?.pending || 0, color: "#f59e0b" },
          { label: "Approved", value: stats?.approved || 0, color: "#16a34a" },
          { label: "Rejected", value: stats?.rejected || 0, color: "#dc2626" },
        ].map((stat) => (
          <Grid item xs={6} md={3} key={stat.label}>
            <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: "16px" }}>
              <Typography color="text.secondary" fontSize={{ xs: 12, md: 14 }}>
                {stat.label}
              </Typography>
              <Typography
                variant="h4"
                fontWeight={700}
                color={stat.color}
                sx={{ fontSize: { xs: "1.5rem", md: "2.125rem" } }}
              >
                {stat.value}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: "18px", mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size={isMobile ? "small" : "medium"}
              placeholder="Search doctor, mobile, registration id"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <Select
              fullWidth
              size={isMobile ? "small" : "medium"}
              displayEmpty
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={6} md={3}>
            <Select
              fullWidth
              size={isMobile ? "small" : "medium"}
              displayEmpty
              value={filters.programmeType}
              onChange={(e) => setFilters({ ...filters, programmeType: e.target.value })}
            >
              <MenuItem value="">All Programmes</MenuItem>
              <MenuItem value="E-Homeopathy">E-Homeopathy</MenuItem>
              <MenuItem value="E-Unani">E-Unani</MenuItem>
              <MenuItem value="E-Ayurveda">E-Ayurveda</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              size={isMobile ? "small" : "medium"}
              options={indianStates}
              getOptionLabel={(option) => option.name || option}
              value={indianStates.find((s) => s.name === filters.state) || null}
              onChange={(_, newValue) =>
                setFilters({ ...filters, state: newValue?.name || "" })
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Filter by state" />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* ── Mobile: Card View ── */}
      {isMobile ? (
        <Box>
          {loading ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress />
            </Box>
          ) : doctors?.length > 0 ? (
            doctors.map((doctor) => (
              <MobileCard key={doctor?._id} doctor={doctor} />
            ))
          ) : (
            <Box py={5} textAlign="center">
              <Typography color="text.secondary">No doctors found</Typography>
            </Box>
          )}
        </Box>
      ) : (
        /* ── Desktop: Table View ── */
        <TableContainer
          component={Paper}
          sx={{ borderRadius: "18px", overflowX: "auto" }}
        >
          <Table sx={{ minWidth: 900 }}>
            <TableHead sx={{ background: "#f1f5f9" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Registration ID</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Doctor Name</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Mobile</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Programme</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Qualification</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>State</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>Submitted</TableCell>
                <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Box py={5}><CircularProgress /></Box>
                  </TableCell>
                </TableRow>
              ) : doctors?.length > 0 ? (
                doctors.map((doctor) => (
                  <TableRow hover key={doctor?._id}>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {doctor?.registrationId}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {doctor?.fullName}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {doctor?.mobileNumber}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {doctor?.programmeType}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {doctor?.qualification}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {doctor?.state}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={doctor?.status}
                        color={getStatusColor(doctor?.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {new Date(doctor?.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center">
                        <Tooltip title="View Details">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate(`/eclinic/${doctor?._id}`)}
                            sx={{ minWidth: 0, px: 1 }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </Button>
                        </Tooltip>
                        {doctor?.status === "Pending" && (
                          <>
                            <Tooltip title="Approve">
                              <Button
                                color="success"
                                variant="contained"
                                size="small"
                                onClick={() => handleApprove(doctor?._id)}
                                sx={{ minWidth: 0, px: 1 }}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </Button>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <Button
                                color="error"
                                variant="contained"
                                size="small"
                                onClick={() => handleReject(doctor?._id)}
                                sx={{ minWidth: 0, px: 1 }}
                              >
                                <CancelIcon fontSize="small" />
                              </Button>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Box py={5}>
                      <Typography color="text.secondary">No doctors found</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
          size={isMobile ? "small" : "medium"}
        />
      </Box>
    </Box>
  );
};

export default EclinicDoctors;